import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateGroup, useDeleteGroup, useRenameGroup } from '../api/mutations';
import { useGroupsQuery } from '../api/queries';
import { ALL_DEVICES_ID } from '../services/groupsService';

type Notify = (severity: string, message: string) => void;

const NO_GROUPS: any[] = [];

/**
 * The group dropdown: the account's groups, which one is selected ("All Devices" is
 * virtual), and create / rename / delete with their error messages.
 */
function useDeviceGroups(notify: Notify) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime' });
    const { data: groups = NO_GROUPS, errorUpdatedAt } = useGroupsQuery();
    const [selectedGroupId, setSelectedGroupId] = useState<string>(ALL_DEVICES_ID);
    const createGroup = useCreateGroup();
    const renameGroup = useRenameGroup();
    const deleteGroup = useDeleteGroup();

    // The list endpoint 500s for vendor accounts. Say so, rather than leave the
    // dropdown silently empty. Keyed on the failure time so each failure shows once.
    useEffect(() => {
        if (errorUpdatedAt) notify('error', t('Groups.loadFailed'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorUpdatedAt]);

    const selectedGroup =
        selectedGroupId === ALL_DEVICES_ID ? null : groups.find((g: any) => g.id === selectedGroupId) ?? null;

    const create = (name: string) =>
        createGroup.mutate(name, {
            onSuccess: ({ group, duplicate }) => {
                if (group?.id) setSelectedGroupId(group.id);
                else notify('error', t(duplicate ? 'Groups.duplicateName' : 'Groups.createFailed'));
            },
            onError: () => notify('error', t('Groups.createFailed')),
        });

    const rename = (id: string, name: string) =>
        renameGroup.mutate(
            { id, name },
            {
                onSuccess: ({ ok, duplicate }) => {
                    if (!ok) notify('error', t(duplicate ? 'Groups.duplicateName' : 'Groups.renameFailed'));
                },
                onError: () => notify('error', t('Groups.renameFailed')),
            }
        );

    const remove = (id: string) =>
        deleteGroup.mutate(id, {
            // Fall back to All Devices if the group being viewed was the one deleted.
            onSuccess: () => setSelectedGroupId((current) => (current === id ? ALL_DEVICES_ID : current)),
            onError: () => notify('error', t('Groups.deleteFailed')),
        });

    return {
        groups,
        selectedGroupId,
        setSelectedGroupId,
        selectedGroup,
        createGroup: create,
        renameGroup: rename,
        deleteGroup: remove,
    };
}

export default useDeviceGroups;
